import { useNavigate } from "@tanstack/react-router";
import type { Store } from "schema/gen/es/chiikipoint/model/v2/model_pb";
import ArrowForward from "~icons/material-symbols/arrow-forward";
import { css } from "../../../styled-system/css";
import Header from "../../components/header";
import WarningBanner from "../../components/warning-banner";
import { t } from "../../libs/i18n";
import { formatNumber } from "../../libs/utils";
import StoreBlock from "./components/store-block";
import type { PayForm } from "./use-pay-form";

type PayInputProps = {
  payFormState: PayForm;
  store: Store;
  paymentFeatureDisabled: boolean;
  onNext: () => void;
};

type BalanceRowProps = {
  balance: PayForm["balances"][number];
  onChangeBalanceAmount: PayForm["onChangeBalanceAmount"];
};

// 入力金額の色を決定する関数
const getBalanceAmountColor = (
  balanceAmount: number,
  userBalanceAmount: number | bigint | undefined,
) => {
  const userValue = userBalanceAmount ? Number(userBalanceAmount) : 0;

  if (balanceAmount > userValue) {
    return "text.accentError";
  }

  if (balanceAmount === userValue) {
    return "text.accentPrimary";
  }

  return "text.secondary";
};

function BalanceRow({ balance, onChangeBalanceAmount }: BalanceRowProps) {
  if (!balance.isAvailable) {
    return (
      <div
        className={css({
          display: "flex",
          flexDirection: "column",
          gap: "4px",
        })}
      >
        <div
          className={css({
            fontSize: "14px",
            lineHeight: "1.4",
            color: "text.disable",
          })}
        >
          {balance.fundSource?.name}
        </div>
        <div
          className={css({
            fontSize: "12px",
            lineHeight: "1.3",
            color: "text.disable",
          })}
        >
          {balance.unavailableReason}
        </div>
      </div>
    );
  }

  return (
    <div
      className={css({
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "8px",
      })}
    >
      <div
        className={css({
          display: "flex",
          flex: "1",
          flexDirection: "column",
          minWidth: "0",
        })}
      >
        <div
          className={css({
            fontSize: "14px",
            lineHeight: "1.4",
            color: "text.secondary",
          })}
        >
          {balance.fundSource?.name}
        </div>
        <div
          className={css({
            display: "flex",
            gap: "8px",
            fontSize: "12px",
            lineHeight: "1.3",
            color: "text.tertiary",
            whiteSpace: "nowrap",
          })}
        >
          <span>{t("残高")}</span>
          <span>{formatNumber(Number(balance.userBalance?.amount))} pt</span>
        </div>
      </div>

      <div
        className={css({
          display: "flex",
          alignItems: "center",
          gap: "8px",
          h: "38px",
          w: "110px",
          px: "14px",
          borderBottom: "1px solid",
          borderColor: "border.secondary",
          flexShrink: 0,
        })}
      >
        <input
          type="tel"
          placeholder="0"
          value={balance.balanceState.amount}
          onChange={(e) => onChangeBalanceAmount(e, balance.fundSourceId)}
          className={css({
            flex: "1",
            textAlign: "right",
            fontSize: "16px",
            lineHeight: "1.5",
            border: "none",
            outline: "none",
            bg: "transparent",
            color: getBalanceAmountColor(
              Number(balance.balanceState.amount),
              balance.userBalance?.amount,
            ),
            minWidth: "0",
          })}
        />
        <span
          className={css({
            fontSize: "14px",
            lineHeight: "1.4",
            fontWeight: "600",
            color: "text.secondary",
          })}
        >
          pt
        </span>
      </div>
    </div>
  );
}

export default function PayInput({
  store,
  onNext,
  paymentFeatureDisabled,
  payFormState,
}: PayInputProps) {
  const navigate = useNavigate();

  return (
    <>
      <Header title={t("支払いポイント入力")} />
      {paymentFeatureDisabled && (
        <WarningBanner
          title={t("支払い機能は利用できません")}
          description={t("利用開始前のため、支払いはできません。")}
        />
      )}

      <div
        className={css({
          px: "20px",
          py: "16px",
          display: "flex",
          flexDirection: "column",
          gap: "24px",
        })}
      >
        <div
          className={css({
            bg: "white",
            rounded: "16px",
            px: "20px",
            pt: "24px",
            pb: "36px",
          })}
        >
          <div
            className={css({
              display: "flex",
              flexDirection: "column",
              gap: "16px",
            })}
          >
            <StoreBlock store={store} size="compact" />
            <div
              className={css({
                bg: "white",
                rounded: "xl",
              })}
            >
              <div
                className={css({
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  border: "1px solid",
                  borderColor: payFormState.errorMessage
                    ? "border.accentError"
                    : "border.secondary",
                  rounded: "12px",
                  px: "14px",
                  h: "48px",
                  boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.03)",
                })}
              >
                <input
                  value={
                    payFormState.payAmount === undefined
                      ? ""
                      : payFormState.payAmount
                  }
                  onChange={payFormState.onChangePayAmount}
                  type="tel"
                  // biome-ignore lint/a11y/noAutofocus: 画面表示時にそのまま入力できるようにしている
                  autoFocus={true}
                  placeholder={t("支払い額を入力")}
                  className={css({
                    fontSize: "16px",
                    lineHeight: "1.5",
                    color: payFormState.errorMessage
                      ? "text.accentError"
                      : payFormState.payAmount === undefined
                        ? "text.disable"
                        : "text.primary",
                    textAlign: "right",
                    width: "100%",
                    px: "4px",
                  })}
                />
                <span
                  className={css({
                    fontSize: "16px",
                    color: "text.primary",
                    fontWeight: "600",
                  })}
                >
                  pt
                </span>
              </div>

              {payFormState.errorMessage && (
                <div
                  className={css({
                    color: "text.accentError",
                    fontSize: "14px",
                    mt: "2px",
                    textAlign: "right",
                  })}
                >
                  {payFormState.errorMessage}
                </div>
              )}
            </div>
          </div>

          <div
            className={css({
              px: "24px",
            })}
          >
            <div
              className={css({
                fontSize: "14px",
                lineHeight: "1.4",
                color: "text.secondary",
                textAlign: "center",
                py: "8px",
                fontWeight: "600",
              })}
            >
              {t("内訳")}
            </div>
            {payFormState.balances.length === 0 && (
              <div
                className={css({
                  textAlign: "center",
                  color: "text.secondary",
                })}
              >
                {t("ポイント残高がありません")}
              </div>
            )}
            <div
              className={css({
                display: "flex",
                flexDirection: "column",
                gap: "12px",
              })}
            >
              {payFormState.balances.map((balance) => (
                <BalanceRow
                  key={balance.fundSource?.id}
                  balance={balance}
                  onChangeBalanceAmount={payFormState.onChangeBalanceAmount}
                />
              ))}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={onNext}
          disabled={
            payFormState.valid === false ||
            paymentFeatureDisabled ||
            !payFormState.hasAvailableBalance
          }
          className={css({
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "8px",
            bg: "surface.accentPrimary",
            color: "white",
            rounded: "12px",
            px: "24px",
            py: "12px",
            fontSize: "16px",
            lineHeight: "1.25",
            fontWeight: "600",
            boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.03)",
            "&:disabled": {
              bg: "surface.disable",
              color: "text.disable",
            },
          })}
        >
          <div>{t("次へ")}</div>
          <ArrowForward
            className={css({
              width: "24px",
              height: "24px",
            })}
          />
        </button>

        <div
          className={css({
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            mt: "12px",
            mb: "16px",
          })}
        >
          <button
            type="button"
            onClick={() => {
              navigate({ to: "/", replace: true });
            }}
            className={css({
              py: "12px",
              px: "16px",
              fontWeight: "600",
            })}
          >
            {t("閉じる")}
          </button>
        </div>
      </div>
    </>
  );
}
